import React from 'react'
import { Button, Grid, Typography, Chip, Box, TextField, Paper, MenuList, MenuItem, ClickAwayListener, CircularProgress } from '@material-ui/core'
import MUIDataTable from 'mui-datatables'
import { useHistory } from 'react-router-dom'
import BackupIcon from '@material-ui/icons/Backup'
import PageTitle from '../../PageTitle/PageTitle'
import { FormLoader } from '../../../Loader/FormLoader'
import { FormattedMessage, useIntl } from 'react-intl'
import Configuration from '../../../../Services/Api/Configuration'
import SubscriptionServices from '../../../../Services/Consumers/SubscriptionServices'
import { filterData } from '../../../../utils/helpers'
import { UsersFiltersPayload } from '../../../../Services/Payloads/AuthenticatedUserPayload'
import { useKeycloak } from '@react-keycloak/web'
import { useStyles } from './Styles'
import UserGroupMenu from './UserGroupMenu'
import { NamedRoutes } from '../../../../routes'
import UserGroupBulkImportForm from './UserGroupBulkImportForm'
import { UserGroupConsumer } from '../../../../UserGroupContext'
import Dialog from '../../Dialog'

export default function UserGroupsContent({ handleNotificationCall, locale }) {
  let classes = useStyles()
  const intl = useIntl()
  const history = useHistory()
  const { keycloak } = useKeycloak()
  const [count, setCount] = React.useState(0)
  const [currentFilter, setCurrentFilter] = React.useState(null)
  const rowsPerPage = Configuration.User_Config.userGroupsLimit || 10
  const [sortOrder, setSortOrder] = React.useState({})
  const [data, setData] = React.useState(['Loading Data...'])
  const [loadedData, setLoadedData] = React.useState([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [canCreate, setCanCreate] = React.useState(false)
  const [reloadPage, setReloadPage] = React.useState(false)

  const [usersSearchText, setUsersSearchText] = React.useState('')
  const [isSearching, setIsSearching] = React.useState(false)
  const [usersSearchResults, setUsersSearchResults] = React.useState([])
  const [showDropdown, setShowDropdown] = React.useState(false)
  const [selectedUser, setSelectedUser] = React.useState({})
  const debounceTimer = React.useRef(null)

  // Bulk import state
  const [isBulkImportDialogOpen, setIsBulkImportDialogOpen] = React.useState(false)

  const columns = [
    {
      name: 'name',
      label: intl.formatMessage({ id: 'UserGroups.List.Table.Name' }),
      options: {
        filterType: 'textField',
        customFilterListOptions: {
          render: (v) => {
            return (
              <div className={classes.FilterChip}>
                <FormattedMessage id='UserGroups.List.Table.Name' />:
                <span>{v}</span>
              </div>
            )
          }
        }
      }
    },
    {
      name: 'users_count',
      label: intl.formatMessage({ id: 'UserGroups.List.Table.UserCount' }),
      options: {
        filter: false
      }
    },
    {
      name: 'users',
      label: intl.formatMessage({ id: 'UserGroups.List.Table.Users' }),
      options: {
        filter: true,
        sort: false,
        filterType: 'custom',
        customFilterListOptions: {
          render: (v) => {
            if (v && v.length > 0 && v[0]) {
              const user = v[0]
              return (
                <div className={classes.FilterChip}>
                  <FormattedMessage id='UserGroups.List.Table.User' />:
                  <span>{user?.first_name} {user?.last_name}</span>
                </div>
              )
            }
            return null
          },
          update: (filterList, filterPos, index) => {
            handleUserRemove()
            filterList[index] = []
            return filterList
          }
        },
        filterOptions: {
          display: (filterList, onChange, index, column) => {
            // Get the selected user from filterList
            const selectedUserInFilter = filterList[index] && filterList[index][0] ? filterList[index][0] : null
            const displayValue = selectedUserInFilter
              ? `${selectedUserInFilter.first_name} ${selectedUserInFilter.last_name}`
              : usersSearchText
            return (
              <div className={classes.userFilterWrapper}>
                <ClickAwayListener onClickAway={() => setShowDropdown(false)}>
                  <div className={classes.autocompleteWrapper}>
                    <TextField
                      label={intl.formatMessage({ id: 'UserGroup.label.SearchUsers' })}
                      placeholder={intl.formatMessage({ id: 'UserGroup.placeholder.SearchUsers' })}
                      fullWidth
                      value={displayValue}
                      onChange={(e) => {
                        // Clear the selected user when typing
                        if (selectedUserInFilter) {
                          filterList[index] = []
                          onChange(filterList[index], index, column)
                          handleUserRemove()
                        }
                        handleSearchChange(e.target.value)
                      }}
                      onFocus={() => {
                        if (usersSearchResults.length > 0) {
                          setShowDropdown(true)
                        }
                      }}
                      helperText={usersSearchText.trim().length === 1 ? intl.formatMessage({ id: 'UserGroups.List.Error.User.Length' }) : !!usersSearchText.trim().length && !Object.keys(selectedUser).length && intl.formatMessage({ id: 'UserGroups.List.Error.User.Empty' })}
                      error={!!usersSearchText.trim().length && !Object.keys(selectedUser).length}
                      InputProps={{
                        endAdornment: isSearching && <CircularProgress size={20} />
                      }}
                    />
                    {/* Dropdown Results */}
                    {showDropdown && (
                      <Paper className={classes.dropdown}>
                        {
                          usersSearchResults.length > 0 ?
                            <MenuList>
                              {usersSearchResults.map((user) => (
                                <MenuItem
                                  key={user.id}
                                  onClick={() => {
                                    handleUserSelect(user)
                                    filterList[index][0] = user
                                    onChange(filterList[index], index, column)
                                  }}
                                  className={classes.menuItem}
                                >
                                  <div>
                                    <div className={classes.userName}>
                                      {user.first_name} {user.last_name}
                                    </div>
                                    <div className={classes.userEmail}>
                                      {user.email}
                                    </div>
                                  </div>
                                </MenuItem>
                              ))}
                            </MenuList>
                            :
                            <Typography variant="body1" className={classes.usersNoResultsText}>
                              {intl.formatMessage({ id: 'UserGroups.List.Users.NoResults' })}
                            </Typography>
                        }
                      </Paper>
                    )}
                  </div>
                </ClickAwayListener>
              </div>
            )
          }
        },
        customBodyRenderLite: (dataIndex) => {
          if (!data[dataIndex] || !data[dataIndex].users) return null

          const users = data[dataIndex].users
          const displayUsers = users.slice(0, 3)
          const hasMore = users.length > 3

          return (
            <Box display="flex" flexWrap="wrap" gap={1}>
              {displayUsers.map((user, index) => (
                <Chip
                  key={index}
                  label={user.name || user.email || user.username}
                  size="small"
                  className={classes.userChip}
                />
              ))}
              {hasMore && (
                <Chip
                  label={`+${users.length - 3} ` + intl.formatMessage({ id: 'UserGroups.List.Table.Users.More' })}
                  size="small"
                  className={classes.moreChip}
                />
              )}
            </Box>
          )
        },
      }
    },
    {
      name: '',
      options: {
        filter: false,
        print: false,
        viewColumns: false,
        download: false,
        customBodyRenderLite: (dataIndex) => {
          return (data[dataIndex] &&
            <UserGroupMenu
              index={dataIndex}
              data={data}
              setData={setData}
              count={count}
              setCount={setCount}
              handleNotificationCall={handleNotificationCall} />)
        }
      }
    }
  ]

  React.useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
    }
  }, [])

  // Handle user search
  const handleUserSearch = async (searchTerm) => {
    if (searchTerm.length < 2) {
      setUsersSearchResults([])
      setShowDropdown(false)
      return
    }
    setIsSearching(true)
    SubscriptionServices.searchUsers({
      headers: { 'Authorization': `Bearer ${keycloak?.token ?? ''}` },
      params: { name: searchTerm }
    }).then((result) => {
      if (!!result && !!result?.data && result?.data?.status?.code === 200) {
        setUsersSearchResults(result.data.data)
        setShowDropdown(true)
      } else {
        let message = intl.formatMessage({ id: 'BasicInfo.FormErrorAlert' })
        if (result.response && result.response.data) {
          if (result.response.data && result.response.data.status && result.response.data.status.errors) {
            message = result.response.data.status.errors
          }
        }
        handleNotificationCall({
          type: 'defence',
          message: message,
          color: 'error'
        }, 'error')
        setUsersSearchResults([])
        setShowDropdown(false)
      }
    }).then(() => {
      setIsSearching(false)
    })
  }

  const handleSearchChange = (value) => {
    setUsersSearchText(value)
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }
    debounceTimer.current = setTimeout(() => {
      handleUserSearch(value)
    }, 500)
  }

  // Handle user selection from dropdown
  const handleUserSelect = (user) => {
    setSelectedUser(user)
    setUsersSearchText('')
    setUsersSearchResults([])
    setShowDropdown(false)
  }

  // Handle user removal
  const handleUserRemove = () => {
    setSelectedUser({})
    setUsersSearchText('')
    const { user_id, ...restFilters } = currentFilter || {}
    setCurrentFilter(Object.keys(restFilters).length > 0 ? restFilters : {})
  }

  // Bulk import handlers
  const handleBulkImportDialogOpen = () => {
    setIsBulkImportDialogOpen(true)
  }

  const handleBulkImportDialogClose = (clearResponse) => {
    setIsBulkImportDialogOpen(false)
    if (clearResponse) {
      clearResponse()
    }
  }

  // get data
  const getUserGroupsListData = async (page) => {
    xhrRequest(page)
  }

  // mock async function
  const xhrRequest = (page, sortOrder = {}, clear) => {
    if (!!keycloak?.authenticated) {
      setIsLoading(true)
      if (!!loadedData[page] && !!loadedData[page].length && !clear) {
        setData(loadedData[page])
        setIsLoading(false)
      } else {
        SubscriptionServices.getUserGroups({
          headers: { 'Authorization': `Bearer ${keycloak?.token ?? ''}` },
          params: filterData(UsersFiltersPayload(page, rowsPerPage, currentFilter))
        }).then((result) => {
          if ((!!result.response && result.response.status !== 200) || !result) {
            let message = intl.formatMessage({ id: 'BasicInfo.FormErrorAlert' })
            if (result.response.data && result.response.data.status) {
              if (result.response.status === 403) {
                setData([])
                message = intl.formatMessage({ id: 'BasicInfo.AccessDeniedAlert' })
              } else if (result.response.data.status.errors && result.response.status === 422) {
                message = result.response.data.status.errors
              } else if (result.response.data.status.message) {
                message = result.response.data.status.message
              }
            }
            handleNotificationCall({
              type: 'defence',
              message: message,
              color: 'error'
            }, 'error')
          } else {
            setCanCreate(true)
            if (!!result && !!result.data && !!result.data.data) {
              let newData = result.data.data
              setSortOrder(sortOrder)
              let sortField = sortOrder.name
              let sortDir = sortOrder.direction

              if (sortField) {
                newData = newData.sort((a, b) => {
                  if (a[sortField] < b[sortField]) {
                    return 1 * (sortDir === 'asc' ? -1 : 1)
                  } else if (a[sortField] > b[sortField]) {
                    return -1 * (sortDir === 'asc' ? -1 : 1)
                  } else {
                    return 0
                  }
                })
              }
              setData(newData)
              let prevData = loadedData
              prevData[page] = newData
              setLoadedData(prevData)
              setCount(result.data.meta.total)
            }
          }
        }).then(() => {
          setIsLoading(false)
        })
      }
    }
  }

  const sort = (page, sortOrder) => {
    setSortOrder(sortOrder)
    let sortField = sortOrder.name
    let sortDir = sortOrder.direction
    let newData = data

    if (sortField) {
      newData = newData.sort((a, b) => {
        if (a[sortField] < b[sortField]) {
          return 1 * (sortDir === 'asc' ? -1 : 1)
        } else if (a[sortField] > b[sortField]) {
          return -1 * (sortDir === 'asc' ? -1 : 1)
        } else {
          return 0
        }
      })
    }
    setData(newData)
  }

  React.useEffect(() => {
    getUserGroupsListData(1)
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [])

  const changePage = (page, sortOrder) => {
    xhrRequest(++page, sortOrder)
  }

  const changeFilter = (applyFilters) => {
    setLoadedData([])
    let filterList = applyFilters()
    let filters = {}
    filterList.forEach((filterItem, index) => {
      if (!!filterItem.length) {
        const columnName = columns[index].name

        // Handle user filter specially (it's an object)
        if (columnName === 'users' && filterItem[0] && typeof filterItem[0] === 'object') {
          filters.user_id = filterItem[0].id
        } else if (filterItem[0] && filterItem[0].length >= 1) {
          filters[columnName] = filterItem[0]
        }
      }
    })

    // Remove 'users' key if it exists (we use user_id instead)
    delete filters.users

    setCurrentFilter(filters)
  }

  React.useEffect(() => {
    if (reloadPage) {
      setLoadedData([])
      xhrRequest(1, {}, true)
      setReloadPage(false)
    }
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [reloadPage])

  React.useEffect(() => {
    if (!!currentFilter) {
      xhrRequest(1, {}, true)
    }
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentFilter])

  const options = {
    filter: true,
    filterType: 'dropdown',
    responsive: 'simple',
    selectableRowsHeader: false,
    selectableRowsHideCheckboxes: true,
    serverSide: true,
    count: count,
    search: false,
    rowsPerPage: rowsPerPage,
    rowsPerPageOptions: [],
    sortOrder: sortOrder,
    confirmFilters: true,
    downloadOptions: { filename: `${intl.formatMessage({ id: 'Sidebar.Links.UserGroups' })}.csv`, separator: ',' },
    customFilterDialogFooter: (currentFilterList, applyNewFilters) => {
      return (
        <div className={classes.ApplyFilterWrapper}>
          <Button color='primary' variant="contained"
            disabled={!!usersSearchText.trim().length && !Object.keys(selectedUser).length}
            onClick={() => changeFilter(applyNewFilters)}>
            <FormattedMessage id='Users.List.Table.ApplyFilters' /></Button>
        </div>
      )
    },
    onFilterChange: (column, filterList, type) => {
      if (type === 'chip') {
        setLoadedData([])
        let newFilters = () => (filterList)
        console.log('updating filters via chip')
        changeFilter(newFilters)
      }
    },
    textLabels: {
      body: {
        noMatch: intl.formatMessage({ id: 'Users.List.Table.NoMatching' }),
        toolTip: intl.formatMessage({ id: 'Users.List.Table.Sort' }),
        columnHeaderTooltip: column => intl.formatMessage(
          { id: 'Users.List.Table.SortFor' },
          { label: column.label })
      },
      pagination: {
        next: intl.formatMessage({ id: 'Users.List.Table.NextPage' }),
        previous: intl.formatMessage({ id: 'Users.List.Table.PreviousPage' }),
        rowsPerPage: intl.formatMessage({ id: 'Users.List.Table.RowsPerPage' }),
        displayRows: intl.formatMessage({ id: 'Users.List.Table.Of' })
      },
      toolbar: {
        search: intl.formatMessage({ id: 'Users.List.Table.Search' }),
        downloadCsv: intl.formatMessage({ id: 'Users.List.Table.Download' }),
        print: intl.formatMessage({ id: 'Users.List.Table.Print' }),
        viewColumns: intl.formatMessage({ id: 'Users.List.Table.ViewColumns' }),
        filterTable: intl.formatMessage({ id: 'Users.List.Table.FilterTable' })
      },
      filter: {
        all: intl.formatMessage({ id: 'Users.List.Table.All' }),
        title: intl.formatMessage({ id: 'Users.List.Table.FILTERS' }),
        reset: intl.formatMessage({ id: 'Users.List.Table.RESET' })
      },
      viewColumns: {
        title: intl.formatMessage({ id: 'Users.List.Table.ShowColumns' }),
        titleAria: intl.formatMessage({ id: 'Users.List.Table.ShowHideColumns' })
      }
    },
    onTableChange: (action, tableState) => {
      switch (action) {
        case 'changePage':
          changePage(tableState.page, tableState.sortOrder)
          break
        case 'sort':
          sort(tableState.page, tableState.sortOrder)
          break
        default:
          break
      }
    }
  }

  return (
    <UserGroupConsumer>
      {({ handleBulkImportSubmit, bulkImportHasError, clearBulkImportError }) => (
        <>
          <PageTitle title={<FormattedMessage id='Sidebar.Links.UserGroups' />}
            button={!!canCreate && (
              <Box display="flex" className={classes.buttonsContainer}>
                <Button
                  variant="outlined"
                  size="large"
                  color="primary"
                  onClick={handleBulkImportDialogOpen}
                  startIcon={<BackupIcon />}
                >
                  <FormattedMessage id='UserGroupBulkImport.button.title' />
                </Button>
                <Button
                  classes={{ root: classes.button }}
                  variant="contained"
                  size="large"
                  color="primary"
                  onClick={() => {
                    history.push(NamedRoutes.subscription.userGroups.add)
                  }}
                >
                  <FormattedMessage id='UserGroups.List.AddUserGroup.Title' />
                </Button>
              </Box>
            )} />
          <Grid container spacing={4}>
            <Grid item xs={12} className={classes.TableWrapper}>
              {isLoading && <FormLoader loading={true} />}
              <MUIDataTable
                title={
                  <Typography variant="h6">
                    <FormattedMessage id='UserGroups.List.Table.Title' />
                  </Typography>
                }
                data={data}
                columns={columns}
                options={options}
              />
            </Grid>
          </Grid>

          {/* Bulk Import Dialog */}
          <Dialog
            isOpen={isBulkImportDialogOpen}
            onClose={() => handleBulkImportDialogClose(clearBulkImportError)}
            maxWidth="sm"
            fullWidth
            title={<FormattedMessage id='UserGroupBulkImport.dialog.title' />}
          >
            <UserGroupBulkImportForm
              onSubmit={handleBulkImportSubmit}
              cancelDialog={() => handleBulkImportDialogClose(clearBulkImportError)}
              bulkImportHasError={bulkImportHasError}
              clearBulkImportError={clearBulkImportError}
              reloadPage={setReloadPage}
            />
          </Dialog>
        </>
      )}
    </UserGroupConsumer>
  )
}